/**
 * MiCarteraPage — Agencias y prospectos asignados al comercial.
 * Click → abre vista 360.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, Chip, Paper, Stack, TextField, MenuItem,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import Agencia360 from './Agencia360';
import {
  API_BASE, getEmpresaId, getAuthHeaders, getCurrentUser,
  PIPELINE_AGENCIA,
} from './format';

function PipelineChip({ estado }) {
  const meta = PIPELINE_AGENCIA.find(p => p.value === estado) || PIPELINE_AGENCIA[3];
  return <Chip size="small" label={meta.label}
    sx={{ bgcolor: `${meta.color}20`, color: meta.color, fontWeight: 700, fontSize: 10, height: 18 }} />;
}

export default function MiCarteraPage() {
  const me = getCurrentUser();
  const [agencias, setAgencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [agencia360Id, setAgencia360Id] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API_BASE}/turismo/agencias?empresa_id=${getEmpresaId()}&limit=500`,
        { headers: getAuthHeaders() }
      );
      const d = await r.json();
      const arr = Array.isArray(d) ? d : (d.agencias || d.items || []);
      // Filtrar por mi asignación si soy comercial
      const role = (me.role || '').toLowerCase();
      const propias = role === 'comercial'
        ? arr.filter(a => String(a.comercial_asignado_user_id || '') === String(me.id))
        : arr;
      setAgencias(propias);
    } catch { setAgencias([]); }
    setLoading(false);
  }, [me.id, me.role]);
  useEffect(() => { cargar(); }, [cargar]);

  const filtradas = agencias.filter(a => {
    if (filtroEstado && a.pipeline_estado !== filtroEstado) return false;
    return true;
  });

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Mi cartera</Typography>
        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
          Agencias asignadas a tu gestión. Click para ver historial 360.
        </Typography>
      </Box>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <TextField select size="small" sx={{ minWidth: 200 }} label="Estado pipeline"
          value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          {PIPELINE_AGENCIA.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
        </TextField>
      </Stack>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress sx={{ color: '#0ea5e9' }} /></Box>
      ) : filtradas.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <BusinessIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>No tienes agencias asignadas aún</Typography>
          <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.5 }}>
            Un administrador puede asignarte agencias desde el panel de Agencias.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 1.5,
        }}>
          {filtradas.map(a => (
            <Paper key={a.id} onClick={() => setAgencia360Id(a.id)}
              sx={{
                p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0',
                cursor: 'pointer',
                '&:hover': { borderColor: '#0ea5e9', boxShadow: '0 4px 12px rgba(14,165,233,0.15)' },
              }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, flex: 1 }}>{a.nombre}</Typography>
                <PipelineChip estado={a.pipeline_estado || 'activa'} />
              </Stack>
              <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                {a.tipo} {a.ciudad && `· ${a.ciudad}`}
              </Typography>
              {a.telefono_general && (
                <Typography sx={{ fontSize: 11, color: '#0d9488' }}>📱 {a.telefono_general}</Typography>
              )}
            </Paper>
          ))}
        </Box>
      )}

      {agencia360Id && (
        <Agencia360 agenciaId={agencia360Id} onClose={() => setAgencia360Id(null)} />
      )}
    </Box>
  );
}
